import React, { useMemo } from 'react';
import { DataLayerGraphProps, DataLayerLegendItem } from '@/types/shared/data-layers.types';

export interface DataLayerGraphViewProps extends DataLayerGraphProps {
  onClose?: () => void;
}

export const DataLayerGraphView: React.FC<DataLayerGraphViewProps> = ({
  graph,
  data,
  legendItems,
  onLegendToggle,
  loading = false,
  className = '',
  onClose,
}) => {
  const enabledItems = useMemo(() => legendItems.filter((item) => item.enabled), [legendItems]);

  if (graph.customComponent) {
    const CustomComponent = graph.customComponent;
    return (
      <CustomComponent
        graph={graph}
        data={data}
        legendItems={legendItems}
        onLegendToggle={onLegendToggle}
        loading={loading}
        className={className}
        onClose={onClose}
      />
    );
  }

  return (
    <div className={`data-layer-graph border rounded-lg p-4 ${className}`}>
      <div className="graph-header flex justify-between items-start mb-4">
        <div>
          <h4 className="font-medium text-gray-900">{graph.title}</h4>
          {graph.unit && <span className="text-sm text-gray-500">Unit: {graph.unit}</span>}
        </div>
        {onClose && (
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600" aria-label="Close graph">
            ✕
          </button>
        )}
      </div>

      {loading ? (
        <div className="graph-loading h-64 flex items-center justify-center bg-gray-50 rounded">
          <div className="text-gray-500">Loading...</div>
        </div>
      ) : (
        <>
          <div className="graph-content h-64 bg-gray-50 rounded mb-4 flex items-center justify-center">
            {data ? (
              <GraphPlaceholder type={graph.type || 'line'} data={data} items={enabledItems} />
            ) : (
              <div className="text-gray-400">No data available</div>
            )}
          </div>

          {legendItems.length > 0 && (
            <div className="graph-legend">
              <h5 className="text-sm font-medium text-gray-700 mb-2">Legend</h5>
              <div className="flex flex-wrap gap-2">
                {legendItems.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => onLegendToggle(item.id)}
                    className={`inline-flex items-center gap-1 px-2 py-1 text-xs rounded transition-all ${
                      item.enabled
                        ? 'bg-blue-100 text-blue-800 hover:bg-blue-200'
                        : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                    }`}
                  >
                    <span className="w-2 h-2 rounded-full" style={{ backgroundColor: item.color || '#999' }} />
                    <span>{item.name}</span>
                    {item.source && <span className="text-[10px] opacity-60">({item.source})</span>}
                  </button>
                ))}
              </div>
            </div>
          )}

          {graph.features && graph.features.length > 0 && (
            <div className="graph-features mt-3">
              <h5 className="text-sm font-medium text-gray-700 mb-2">Features</h5>
              <div className="flex flex-wrap gap-1">
                {graph.features.map((feature) => (
                  <span key={feature} className="inline-block px-2 py-1 text-xs bg-gray-100 rounded">
                    {feature}
                  </span>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

interface GraphPlaceholderProps {
  type: string;
  data: any;
  items: DataLayerLegendItem[];
}

const GraphPlaceholder: React.FC<GraphPlaceholderProps> = ({ type, data, items }) => {
  const graphTypeDisplay = {
    line: '📈 Line Chart',
    bar: '📊 Bar Chart',
    area: '📉 Area Chart',
    scatter: '⚫ Scatter Plot',
    heatmap: '🟥 Heatmap',
    custom: '📊 Custom Chart',
  };

  return (
    <div className="text-center">
      <div className="text-2xl mb-2">{graphTypeDisplay[type as keyof typeof graphTypeDisplay] || '📊 Chart'}</div>
      <div className="text-sm text-gray-500">{items.length} series selected</div>
      {data && typeof data === 'object' && (
        <div className="text-xs text-gray-400 mt-2">
          Data points: {Array.isArray(data) ? data.length : Object.keys(data).length}
        </div>
      )}
    </div>
  );
};
